import { useState } from "react";

import type { Track } from "../../api/projects";
import { useEditor } from "../../store/editor";

export default function TrackHeader(props: {
  track: Track;
  height: number;
  isFirst: boolean;
  isLast: boolean;
}) {
  const { track, height, isFirst, isLast } = props;
  const renameTrack = useEditor((s) => s.renameTrack);
  const removeTrack = useEditor((s) => s.removeTrack);
  const reorderTrack = useEditor((s) => s.reorderTrack);
  const setTrackFlag = useEditor((s) => s.setTrackFlag);
  const muted = (track as any).muted === true;
  const solo = (track as any).solo === true;
  const ducked = (track as any).ducked === true;
  const isAudio = track.type === "audio";

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(track.name || track.id.toUpperCase());

  function commit() {
    renameTrack(track.id, draft.trim() || track.id.toUpperCase());
    setEditing(false);
  }

  function onDelete() {
    const n = track.clips.length;
    if (n > 0 && !confirm(`Borrar pista "${track.name || track.id}" y sus ${n} clip(s)?`)) return;
    removeTrack(track.id);
  }

  return (
    <div style={{
      height,
      borderBottom: "1px solid var(--border)",
      display: "flex", flexDirection: "column", justifyContent: "space-between",
      padding: "4px 6px",
      background: track.type === "audio" ? "rgba(91,199,255,0.04)" : undefined,
      fontSize: 11, color: "var(--text-dim)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 9, opacity: 0.6 }}>
          {track.type === "video" ? "▦" : "♪"}
        </span>
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") setEditing(false);
            }}
            style={{ padding: "1px 4px", fontSize: 11, flex: 1, minWidth: 0 }}
          />
        ) : (
          <span
            onDoubleClick={() => { setDraft(track.name || track.id.toUpperCase()); setEditing(true); }}
            title="Doble click para renombrar"
            style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                     color: "var(--text)", cursor: "text" }}
          >
            {track.name || track.id.toUpperCase()}
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
        <button
          onClick={() => setTrackFlag(track.id, "muted", !muted)}
          style={{
            padding: "0 5px", fontSize: 10, height: 18, lineHeight: 1,
            background: muted ? "var(--danger)" : undefined,
            color: muted ? "white" : undefined,
            borderColor: muted ? "var(--danger)" : undefined,
          }}
          title={muted ? "Quitar silencio" : "Silenciar pista"}
        >M</button>
        <button
          onClick={() => setTrackFlag(track.id, "solo", !solo)}
          style={{
            padding: "0 5px", fontSize: 10, height: 18, lineHeight: 1,
            background: solo ? "#f5d04a" : undefined,
            color: solo ? "#000" : undefined,
            borderColor: solo ? "#f5d04a" : undefined,
          }}
          title={solo ? "Quitar solo" : "Sólo esta pista"}
        >S</button>
        {isAudio && (
          <button
            onClick={() => setTrackFlag(track.id, "ducked" as any, !ducked)}
            style={{
              padding: "0 5px", fontSize: 10, height: 18, lineHeight: 1,
              background: ducked ? "#fb923c" : undefined,
              color: ducked ? "#3d1f06" : undefined,
              borderColor: ducked ? "#fb923c" : undefined,
            }}
            title={ducked ? "Quitar ducking" : "Ducking: bajar volumen cuando otra pista de audio suena"}
          >🦆</button>
        )}
        <button
          onClick={() => reorderTrack(track.id, "up")}
          disabled={isFirst}
          style={{ padding: "0 4px", fontSize: 10, height: 18, lineHeight: 1 }}
          title="Subir (más visible)"
        >▲</button>
        <button
          onClick={() => reorderTrack(track.id, "down")}
          disabled={isLast}
          style={{ padding: "0 4px", fontSize: 10, height: 18, lineHeight: 1 }}
          title="Bajar"
        >▼</button>
        <button
          onClick={onDelete}
          className="danger"
          style={{ padding: "0 5px", fontSize: 11, height: 18, lineHeight: 1, marginLeft: "auto" }}
          title="Eliminar pista"
        >×</button>
      </div>
    </div>
  );
}
